/**
 * ToolFeedbackButton — a small reusable button any tool component can drop
 * into its header so users can post a note straight into the tool's
 * feedback thread. Anyone signed in can submit.
 *
 * Usage:
 *   <ToolFeedbackButton toolSlug="label-maker" />
 *
 * Behind the scenes it hits `POST /api/tools/{slug}/feedback` — same
 * endpoint the admin workstation drawer reads from.
 */

import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageSquarePlus, X, Send } from 'lucide-react';

export default function ToolFeedbackButton({ toolSlug, label = 'Send feedback' }) {
  const { API, user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const submit = async () => {
    const text = message.trim();
    if (!text) {
      toast.error('Please add a note before sending.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tools/${toolSlug}/feedback`, { message: text });
      toast.success('Thanks — your note went straight to the tool owner.');
      setMessage('');
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send feedback');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`tool-feedback-btn-${toolSlug}`}
      >
        <MessageSquarePlus className="w-4 h-4 mr-1.5" /> {label}
      </Button>

      {open ? (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-center justify-center p-4"
          onClick={() => !saving && setOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-background border border-border rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b border-border">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
                  Send feedback
                </div>
                <div className="font-bold text-base mt-0.5">
                  What&apos;s working, broken, or missing?
                </div>
              </div>
              <button
                type="button"
                onClick={() => !saving && setOpen(false)}
                className="text-slate-400 hover:text-foreground"
                data-testid="tool-feedback-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <Textarea
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Bugs, missing fields, ideas — anything. The tool owner reads every note."
                data-testid="tool-feedback-textarea"
                autoFocus
              />
              <p className="text-[11px] text-slate-500">
                {message.length}/4000 characters
              </p>
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={saving || !message.trim()} data-testid="tool-feedback-submit">
                <Send className="w-4 h-4 mr-1.5" /> {saving ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
